import type { KpiCatalogEntry, KpiPolarity } from "@nexus/contracts"
import { resolveCatalogColorVar } from "@/features/kpi-settings/lib/kpi-color"
import type { TableValueFormat } from "./format-table-value"

export type MyBrandNumericField = string

export interface MyBrandKpiCatalogEntry {
  /** Stable id — persisted in the store/URL; canonical `Kpi.name`. */
  id: string
  label: string
  format: TableValueFormat
  decimals: number
  /** CSS var (e.g. "var(--chart-1)") resolved from `colorToken`. */
  colorVar: string
  polarity: KpiPolarity
  /** Share-of-total formulas (`Spend / SUM("Spend")`) never get a card trend. */
  hasSumFormula: boolean
}

function toTableFormat(entry: KpiCatalogEntry): TableValueFormat {
  if (entry.name === "ROAS") return "ratio"
  if (entry.format === "CURRENCY") return "currency"
  if (entry.format === "PERCENT") return "percent"
  return "number"
}

function formulaHasSum(formula: string | null): boolean {
  return formula != null && /SUM\s*\(/i.test(formula)
}

export function buildMyBrandKpiCatalog(rows: KpiCatalogEntry[]): MyBrandKpiCatalogEntry[] {
  const visible = rows.filter((row) => row.visible)
  return visible.map((row) => ({
    id: row.name,
    label: row.displayName,
    format: toTableFormat(row),
    decimals: row.decimals,
    colorVar: resolveCatalogColorVar(row.name, row.index, row.colorToken),
    polarity: row.polarity,
    hasSumFormula: formulaHasSum(row.formula),
  }))
}

export function myBrandCatalogById(
  catalog: MyBrandKpiCatalogEntry[],
): Record<string, MyBrandKpiCatalogEntry> {
  return Object.fromEntries(catalog.map((e) => [e.id, e]))
}

/**
 * KPIs the My Brand report should actually show. Membership is the visible
 * catalog, not the org-union map: a KPI the user hid is still in that map, and
 * rendering it leaves a column with no way to remove it. An empty catalog means
 * settings are still loading — keep the selection so the page does not flicker.
 * When every selected id is absent, fall back to the first catalog KPI so the
 * report is never blank.
 */
export function resolveMyBrandKpis(
  selected: string[],
  catalog: MyBrandKpiCatalogEntry[],
): string[] {
  if (catalog.length === 0) return selected
  const visibleIds = new Set(catalog.map((entry) => entry.id))
  const resolved = selected.filter((id) => visibleIds.has(id))
  if (resolved.length > 0) return resolved
  return [catalog[0].id]
}

/** Default KPI selection for a fresh My Brand drilldown session. */
export const DEFAULT_MY_BRAND_KPIS: string[] = ["Spend", "CPA", "ROAS"]

/** @deprecated Use `buildMyBrandKpiCatalog` from the API catalog. */
export const MY_BRAND_KPI_CATALOG: MyBrandKpiCatalogEntry[] = []
export const MY_BRAND_KPI_CATALOG_BY_ID: Record<string, MyBrandKpiCatalogEntry> = {}
